/**
 * Imperative open / close / toggle hooks. Backed by the singleton
 * agent-store so any host CTA — even outside an `<Agent…>` subtree —
 * can pop the topbar Sheet.
 */

import { useCallback } from 'react'

import { useAgentStore } from '../stores/agent-store'

/**
 * The mounted search's own open, which focuses its input synchronously.
 * iOS Safari only raises the keyboard for a focus() made inside the tap
 * itself; flipping the store flag and focusing on a later render loses the
 * gesture, so the phone search circle opened with no keyboard.
 */
let searchOpener: (() => void) | null = null

/** Called by `AgentSearch` while mounted; returns the unregister. */
export function registerAgentSearchOpener(opener: () => void): () => void {
  searchOpener = opener
  return () => {
    if (searchOpener === opener) {
      searchOpener = null
    }
  }
}

/** Open through the mounted search when there is one, else just the store flag. Exported for tests. */
export function openAgent(setOpen: (open: boolean) => void): void {
  if (searchOpener) {
    searchOpener()
    return
  }
  setOpen(true)
}

export function useAgentOpen(): () => void {
  const setOpen = useAgentStore((s) => s.setOpen)
  return useCallback(() => openAgent(setOpen), [setOpen])
}

export function useAgentClose(): () => void {
  const setOpen = useAgentStore((s) => s.setOpen)
  return useCallback(() => setOpen(false), [setOpen])
}

export function useAgentToggle(): () => void {
  const toggle = useAgentStore((s) => s.toggle)
  return useCallback(() => toggle(), [toggle])
}
